import React from 'react';
import { Text, View } from 'react-native';
import { ThemeColors } from '@/src/context/ThemeContext';
import { BlogPost } from '@/src/domain/models/BlogPost';

const Content: React.FC<{ colors: ThemeColors }> = ({ colors }) => (
  <View>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 20 }}>
      Seeding determines who plays whom in your bracket — and when. A well-seeded
      tournament puts the best players on a collision course only in the later rounds,
      rewards consistency, and prevents great players from canceling each other out in
      round one. A poorly seeded bracket feels unfair before the first match is played.
      This guide explains every seeding method, how to place seeds in a bracket, and
      when to use each approach.
    </Text>

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 8 }}>
      What is tournament seeding?
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 20 }}>
      A seed is a ranking number assigned to each participant before the tournament
      begins. Seed 1 is the strongest expected competitor, seed 2 is the second
      strongest, and so on. Seeds are then placed into specific bracket positions
      so that the top seeds can only meet each other in the final rounds — assuming
      they win every match along the way. Seeding does not guarantee outcomes; it
      creates a structure where the expected best matchups happen at the highest
      stakes moments.
    </Text>

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 8 }}>
      Step 1: Assign seeds to every participant
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 12 }}>
      Before placing anyone in the bracket, rank every participant. Four methods
      work depending on context:
    </Text>
    {[
      {
        label: 'Previous results',
        desc: 'Best method when participants have played each other before. Use win/loss record, head-to-head results, or standings from a prior season. Most professional leagues seed playoff brackets this way.',
      },
      {
        label: 'Skill rating or ELO',
        desc: 'Use for games or sports with formal rating systems (chess ELO, video game MMR, USTA tennis rating). Objective and hard to argue with.',
      },
      {
        label: 'Organizer ranking',
        desc: 'Organizer assigns seeds based on observed skill, reputation, or qualifying performance. Works for casual events with no formal records. Requires transparency — announce seeds before the bracket is drawn.',
      },
      {
        label: 'Random draw',
        desc: 'Every participant gets a random seed number. Use when all participants are equal skill (recreational leagues), when you have no performance data, or when you want pure fairness without subjectivity.',
      },
    ].map((item, index) => (
      <View key={index} style={{ marginBottom: 16 }}>
        <Text style={{ fontSize: 15, fontWeight: '700', color: colors.text, marginBottom: 4 }}>{item.label}</Text>
        <Text style={{ fontSize: 15, lineHeight: 23, color: colors.textSecondary }}>{item.desc}</Text>
      </View>
    ))}

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 16 }}>
      Step 2: Place seeds in the bracket
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 12 }}>
      Standard bracket seeding follows a mirroring rule: the top seed and the bottom
      seed are placed on opposite halves of the bracket so they can only meet in the
      final. Within each half, the same logic repeats recursively. For a 16-player
      bracket the first-round matchups are:
    </Text>
    {[
      '1 vs 16',
      '8 vs 9',
      '5 vs 12',
      '4 vs 13',
      '6 vs 11',
      '3 vs 14',
      '7 vs 10',
      '2 vs 15',
    ].map((match, index) => (
      <View key={index} style={{ flexDirection: 'row', marginBottom: 6, alignItems: 'center' }}>
        <Text style={{ color: colors.primary, marginRight: 10, fontSize: 14, fontWeight: '700' }}>{index + 1}.</Text>
        <Text style={{ fontSize: 15, color: colors.textSecondary, fontFamily: 'monospace' }}>{match}</Text>
      </View>
    ))}
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginTop: 16, marginBottom: 20 }}>
      This placement guarantees: seeds 1 and 2 can only meet in the final, seeds 1–4
      can only meet in the semifinals, and seeds 1–8 can only meet in the quarterfinals.
      The pattern scales to any power-of-2 bracket size (8, 16, 32, 64, 128).
    </Text>

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 8 }}>
      Handling non-power-of-2 field sizes (byes)
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 20 }}>
      Brackets must have 8, 16, 32, or 64 slots. If you have 11 participants, you
      need a 16-player bracket with 5 byes. Always assign byes to the top seeds —
      seed 1 gets a bye before seed 2, seed 2 before seed 3, and so on. This rewards
      top seeds with an easier path to the second round and keeps the final matchups
      clean. Never assign byes randomly when you have seeded participants.
    </Text>

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 8 }}>
      Seeding in double elimination brackets
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 20 }}>
      Double elimination uses the same initial seed placement as single elimination.
      The key difference is the losers bracket: when a seeded player loses their first
      match, they drop into the losers bracket rather than being eliminated. Top seeds
      who lose early will still face each other in the losers bracket because the loser
      bracket is seeded to keep strong players apart as long as possible. This means
      a 1 vs 2 final is still possible even if one of them lost in round one.
    </Text>

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 8 }}>
      Seeding in round robin and Swiss
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 20 }}>
      Round robin does not use seeding the same way. Every player faces every other
      player, so there is no path manipulation — the strongest player wins by beating
      everyone. Seeds are still useful for display order and for seeding a follow-up
      playoff bracket from round robin standings. Swiss system uses seeds to create the
      first-round pairings: seed 1 plays seed 2, seed 3 plays seed 4, and so on. After
      round one, pairings are based on current score rather than initial seed.
    </Text>

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 8 }}>
      Common seeding mistakes
    </Text>
    {[
      'Placing top seeds on the same half of the bracket — they collide in the semifinals instead of the final.',
      'Assigning byes to low seeds — this is a prize that belongs to the top performers.',
      'Announcing seeds after the bracket is drawn — seeds must be public before placement so participants can verify fairness.',
      'Using seeding in round robin when the goal is just display order — seeds do not change who plays whom.',
      'Seeding a small field of 4–6 players with high precision — with so few matches, the difference between seeds 3 and 4 barely matters.',
    ].map((item, index) => (
      <View key={index} style={{ flexDirection: 'row', marginBottom: 10, alignItems: 'flex-start' }}>
        <Text style={{ color: colors.primary, marginRight: 10, fontSize: 16 }}>•</Text>
        <Text style={{ fontSize: 15, lineHeight: 23, color: colors.textSecondary, flex: 1 }}>{item}</Text>
      </View>
    ))}

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 24 }}>
      Seeding in Tournaly
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 16 }}>
      When you create a single or double elimination tournament in Tournaly, participants
      are assigned seed numbers in the order you add them. You can reorder participants
      before generating the bracket to control seed placement. Tournaly then applies
      standard mirrored seeding automatically — seed 1 vs the lowest seed, seed 2 vs
      the second-lowest, with top seeds on opposite halves. Byes are assigned to the
      top seeds when the field is not a power of two. Generate the bracket, share the
      link, and let everyone see exactly who plays whom from the start.
    </Text>
  </View>
);

const ContentEs: React.FC<{ colors: ThemeColors }> = ({ colors }) => (
  <View>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 20 }}>
      El seeding determina quién juega contra quién en el bracket — y cuándo. Un torneo
      bien sembrado pone a los mejores jugadores en ruta de colisión solo en las rondas
      finales, recompensa la consistencia y evita que grandes jugadores se eliminen entre
      sí en la primera ronda. Un bracket mal sembrado se siente injusto antes de que
      se juegue el primer partido. Esta guía explica cada método de seeding, cómo colocar
      las cabezas de serie en el bracket y cuándo usar cada enfoque.
    </Text>

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 8 }}>
      ¿Qué es el seeding en un torneo?
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 20 }}>
      Un seed (cabeza de serie) es un número de clasificación asignado a cada participante
      antes de que comience el torneo. El seed 1 es el competidor más fuerte esperado,
      el seed 2 es el segundo más fuerte, y así sucesivamente. Los seeds se colocan luego
      en posiciones específicas del bracket para que las cabezas de serie solo puedan
      enfrentarse entre sí en las rondas finales — asumiendo que ganen todos sus partidos.
      El seeding no garantiza resultados; crea una estructura donde los mejores enfrentamientos
      esperados ocurren en los momentos de mayor tensión.
    </Text>

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 8 }}>
      Paso 1: Asignar seeds a cada participante
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 12 }}>
      Antes de colocar a nadie en el bracket, clasifica a todos los participantes.
      Cuatro métodos funcionan según el contexto:
    </Text>
    {[
      {
        label: 'Resultados anteriores',
        desc: 'Mejor método cuando los participantes ya se han enfrentado antes. Usa registro de victorias/derrotas, resultados directos o clasificaciones de una temporada anterior. La mayoría de las ligas profesionales usan este método para sembrar los brackets de playoffs.',
      },
      {
        label: 'Rating de habilidad o ELO',
        desc: 'Útil para juegos o deportes con sistemas de rating formales (ELO de ajedrez, MMR de videojuegos, rating RFET de tenis). Objetivo y difícil de discutir.',
      },
      {
        label: 'Clasificación del organizador',
        desc: 'El organizador asigna seeds basándose en habilidad observada, reputación o rendimiento clasificatorio. Funciona para eventos casuales sin registros formales. Requiere transparencia — anuncia los seeds antes de sortear el bracket.',
      },
      {
        label: 'Sorteo aleatorio',
        desc: 'Cada participante recibe un número de seed aleatorio. Úsalo cuando todos los participantes tienen habilidad similar (ligas recreativas), cuando no tienes datos de rendimiento, o cuando quieres equidad pura sin subjetividad.',
      },
    ].map((item, index) => (
      <View key={index} style={{ marginBottom: 16 }}>
        <Text style={{ fontSize: 15, fontWeight: '700', color: colors.text, marginBottom: 4 }}>{item.label}</Text>
        <Text style={{ fontSize: 15, lineHeight: 23, color: colors.textSecondary }}>{item.desc}</Text>
      </View>
    ))}

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 16 }}>
      Paso 2: Colocar las cabezas de serie en el bracket
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 12 }}>
      El seeding estándar sigue una regla de espejado: la cabeza de serie más alta y
      la más baja se colocan en mitades opuestas del bracket para que solo puedan
      encontrarse en la final. Dentro de cada mitad, la misma lógica se repite
      recursivamente. Para un bracket de 16 jugadores, los enfrentamientos de primera
      ronda son:
    </Text>
    {[
      '1 vs 16',
      '8 vs 9',
      '5 vs 12',
      '4 vs 13',
      '6 vs 11',
      '3 vs 14',
      '7 vs 10',
      '2 vs 15',
    ].map((match, index) => (
      <View key={index} style={{ flexDirection: 'row', marginBottom: 6, alignItems: 'center' }}>
        <Text style={{ color: colors.primary, marginRight: 10, fontSize: 14, fontWeight: '700' }}>{index + 1}.</Text>
        <Text style={{ fontSize: 15, color: colors.textSecondary, fontFamily: 'monospace' }}>{match}</Text>
      </View>
    ))}
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginTop: 16, marginBottom: 20 }}>
      Esta colocación garantiza: los seeds 1 y 2 solo pueden verse en la final, los
      seeds 1–4 solo pueden verse en semifinales, y los seeds 1–8 solo pueden verse en
      cuartos de final. El patrón escala a cualquier tamaño de bracket potencia de 2
      (8, 16, 32, 64, 128).
    </Text>

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 8 }}>
      Tamaños de campo no potencia de 2 (byes)
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 20 }}>
      Los brackets deben tener 8, 16, 32 o 64 plazas. Si tienes 11 participantes,
      necesitas un bracket de 16 jugadores con 5 byes. Siempre asigna los byes a
      las cabezas de serie más altas — el seed 1 recibe bye antes que el seed 2,
      el seed 2 antes que el seed 3, y así sucesivamente. Esto recompensa a las
      mejores cabezas de serie con un camino más fácil hacia la segunda ronda y
      mantiene los enfrentamientos finales limpios. Nunca asignes byes aleatoriamente
      cuando tienes participantes sembrados.
    </Text>

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 8 }}>
      Seeding en brackets de doble eliminación
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 20 }}>
      La doble eliminación usa la misma colocación inicial de seeds que la eliminación
      simple. La diferencia clave está en el bracket de perdedores: cuando un jugador
      sembrado pierde su primer partido, cae al bracket de perdedores en lugar de
      ser eliminado. Las cabezas de serie más altas que pierden pronto seguirán
      enfrentándose entre sí en el bracket de perdedores, ya que este también está
      sembrado para mantener a los jugadores fuertes separados el mayor tiempo posible.
      Esto significa que una final entre el 1 y el 2 sigue siendo posible aunque uno
      de ellos haya perdido en la primera ronda.
    </Text>

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 8 }}>
      Seeding en round robin y sistema suizo
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 20 }}>
      El round robin no usa el seeding de la misma manera. Cada jugador se enfrenta
      a todos los demás, así que no hay manipulación de caminos — el jugador más
      fuerte gana derrotando a todos. Los seeds siguen siendo útiles para el orden
      de visualización y para sembrar un bracket de playoffs posterior desde las
      clasificaciones del round robin. El sistema suizo usa seeds para crear los
      emparejamientos de la primera ronda: el seed 1 juega contra el seed 2, el seed 3
      contra el seed 4, y así sucesivamente. Después de la primera ronda, los
      emparejamientos se basan en la puntuación actual en lugar del seed inicial.
    </Text>

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 8 }}>
      Errores comunes de seeding
    </Text>
    {[
      'Colocar las cabezas de serie más altas en la misma mitad del bracket — se enfrentan en semifinales en lugar de en la final.',
      'Asignar byes a seeds bajos — ese privilegio le corresponde a los mejores clasificados.',
      'Anunciar los seeds después de sortear el bracket — los seeds deben ser públicos antes de la colocación para que los participantes puedan verificar la equidad.',
      'Usar seeding en round robin cuando el objetivo es solo el orden de visualización — los seeds no cambian quién juega contra quién.',
      'Sembrar con alta precisión un campo pequeño de 4–6 jugadores — con tan pocos partidos, la diferencia entre los seeds 3 y 4 apenas importa.',
    ].map((item, index) => (
      <View key={index} style={{ flexDirection: 'row', marginBottom: 10, alignItems: 'flex-start' }}>
        <Text style={{ color: colors.primary, marginRight: 10, fontSize: 16 }}>•</Text>
        <Text style={{ fontSize: 15, lineHeight: 23, color: colors.textSecondary, flex: 1 }}>{item}</Text>
      </View>
    ))}

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 24 }}>
      Seeding en Tournaly
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 16 }}>
      Cuando creas un torneo de eliminación simple o doble en Tournaly, los participantes
      reciben números de seed en el orden en que los añades. Puedes reordenar los
      participantes antes de generar el bracket para controlar la colocación de seeds.
      Tournaly aplica entonces el seeding espejado estándar automáticamente — seed 1
      contra el seed más bajo, seed 2 contra el segundo más bajo, con las cabezas de
      serie más altas en mitades opuestas. Los byes se asignan a las cabezas de serie
      más altas cuando el campo no es potencia de dos. Genera el bracket, comparte el
      enlace y deja que todos vean exactamente quién juega contra quién desde el principio.
    </Text>
  </View>
);

export const post: BlogPost = {
  slug: '2026-05-19-how-to-seed-a-tournament-bracket',
  title: 'How to Seed a Tournament Bracket: A Complete Guide',
  titleEs: 'Cómo Sembrar un Bracket de Torneo: Guía Completa',
  date: '2026-05-19',
  summary:
    'Learn how to assign seeds, place them correctly in a bracket using standard mirrored seeding, handle byes, and avoid the most common seeding mistakes — for single elimination, double elimination, and Swiss tournaments.',
  summaryEs:
    'Aprende a asignar seeds, colocarlos correctamente en un bracket con seeding espejado estándar, gestionar byes y evitar los errores más comunes — para torneos de eliminación simple, doble eliminación y sistema suizo.',
  tags: ['Guide', 'Seeding', 'Bracket', 'Single Elimination', 'Double Elimination'],
  Content,
  ContentEs,
};
